import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getCallGate, getDealCalls, getJob, getTelephonySettings, requestCall } from '@/api/crmCard';
import { crmKeys } from '@/hooks/useDealCard';
import { runPbxCall, type CallFlowEvent } from '@/lib/callFlow';
import { callMode, gateMessage, NO_LOGIN_TEXT, OWN_PBX_TEXT, telHref, type CallMode } from '@/lib/callMode';
import { useProjectStore } from '@/store/project';

export interface CallUi {
  phone: string;
  name: string;
  step: 0 | 1 | 2;
  /** Секунды разговора (с шага 2). */
  seconds: number;
  ended: boolean;
  error: string | null;
  note: string | null;
}

export interface CallNotice {
  /** Что случилось словами. */
  text: string;
  /** Предложить обычный звонок с телефона. */
  tel: string | null;
}

/**
 * Звонок из карточки/переписки. Выбирает путь по настройкам телефонии CRM (callMode), проверяет запрет
 * «сначала решите по прошлым звонкам» и для обратного вызова через АТС ведёт наложение с шагами.
 */
export function useCallFlow(input: { dealId?: string; personId?: string; name: string }) {
  const qc = useQueryClient();
  const project = useProjectStore((s) => s.activeProjectId);
  const [ui, setUi] = useState<CallUi | null>(null);
  const [notice, setNotice] = useState<CallNotice | null>(null);
  const [starting, setStarting] = useState(false);
  const cancelled = useRef(false);
  const talkStart = useRef<number | null>(null);

  useEffect(() => () => { cancelled.current = true; }, []);

  // Таймер разговора.
  useEffect(() => {
    if (!ui || ui.step < 2 || ui.ended || ui.error) return;
    const t = setInterval(() => {
      setUi((u) => (u && talkStart.current ? { ...u, seconds: Math.floor((Date.now() - talkStart.current) / 1000) } : u));
    }, 1000);
    return () => clearInterval(t);
  }, [ui?.step, ui?.ended, ui?.error]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = useCallback((e: CallFlowEvent) => {
    setUi((u) => {
      if (!u) return u;
      switch (e.kind) {
        case 'working':
          if (e.step === 2 && talkStart.current === null) talkStart.current = Date.now();
          return { ...u, step: e.step, error: null };
        case 'ended': return { ...u, ended: true };
        case 'failed': return { ...u, error: e.message };
        case 'untracked': return { ...u, note: e.note };
      }
    });
  }, []);

  /** `over` — звонок другому клиенту с того же экрана (строка журнала «Перезвонить»): сделка, человек, имя. */
  const start = useCallback(async (phone: string | null, over?: { dealId?: string; personId?: string; name?: string }) => {
    const inp = { ...input, ...over };
    if (!phone) { setNotice({ text: 'У клиента не указан номер телефона', tel: null }); return; }
    setStarting(true);
    cancelled.current = false;
    try {
      // Запрет по итогам звонков действует на любом пути — как в веб-CRM (CallButton → canCallNow).
      try {
        const gate = await getCallGate();
        if (gate.blocked) { setNotice({ text: gateMessage(gate), tel: null }); return; }
      } catch { /* не смогли спросить — пускаем: сервер АТС всё равно держит тот же запрет */ }

      const settings = await qc.fetchQuery({ queryKey: crmKeys.telephony(project), queryFn: getTelephonySettings, staleTime: 5 * 60_000 }).catch(() => null);
      const mode: CallMode = callMode(settings);
      if (mode === 'noLogin') { setNotice({ text: NO_LOGIN_TEXT, tel: telHref(phone) }); return; }
      if (mode === 'own') { setNotice({ text: OWN_PBX_TEXT, tel: telHref(phone) }); return; }
      if (mode === 'device') { window.location.href = telHref(phone); return; }

      talkStart.current = null;
      setUi({ phone, name: inp.name ?? '', step: 0, seconds: 0, ended: false, error: null, note: null });
      await runPbxCall(
        { phone, dealId: inp.dealId, personId: inp.personId },
        {
          requestCall, getJob, getDealCalls: (id) => getDealCalls(id, 1),
          sleep: (ms) => new Promise((r) => setTimeout(r, ms)), now: () => Date.now(),
        },
        apply,
        () => cancelled.current,
      );
    } finally {
      setStarting(false);
    }
  }, [qc, project, input.dealId, input.personId, input.name, apply]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = useCallback(() => { cancelled.current = true; talkStart.current = null; setUi(null); }, []);
  const closeNotice = useCallback(() => setNotice(null), []);

  return { ui, notice, starting, start, close, closeNotice };
}
